import { useEffect, useState } from "react";
import { api, errMsg, FRAMEWORK_META, ACCENT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { WarningCircle, FloppyDisk, Trash } from "@phosphor-icons/react";

const FRAMEWORKS = ["panchatanthra", "viveka_kartha", "dinacharya"];

export default function AdminWorkbooks() {
  const { tenantId } = useAuth();
  const [versions, setVersions] = useState([]);
  const [sources, setSources] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [form, setForm] = useState({ framework: "panchatanthra", label: "", sheet_name: "", url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/questionnaires`).then(({ data }) => setVersions(data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/tenants/${tenantId}/workbook-sources`).then(({ data }) => setSources(data)).catch(() => {});
  };
  useEffect(load, [tenantId]); // eslint-disable-line

  const openEditor = async (id) => {
    try {
      const { data } = await api.get(`/tenants/${tenantId}/questionnaires/${id}`);
      setEditing(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const uploadWorkbook = async () => {
    if (!file) return toast.error("Choose an .xlsx workbook");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("framework", form.framework);
    fd.append("label", form.label || file.name.replace(/\.xlsx?$/i, ""));
    if (form.sheet_name) fd.append("sheet_name", form.sheet_name);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/questionnaires/import-upload`, fd);
      toast.success(`Imported ${data.import_stats.questions} questions across ${data.import_stats.elements} elements`);
      setUploadOpen(false);
      setFile(null);
      load();
      setEditing(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const importLink = async () => {
    setBusy(true);
    const folder = /\/folders\//.test(form.url);
    try {
      const { data } = folder
        ? await api.post(`/tenants/${tenantId}/questionnaires/import-folder`, {
            framework: form.framework,
            label: form.label || "Imported workbooks",
            folder_url: form.url,
          })
        : await api.post(`/tenants/${tenantId}/questionnaires/import-link`, {
            framework: form.framework,
            label: form.label || "Imported workbook",
            source_url: form.url,
            sheet_name: form.sheet_name || null,
          });
      toast.success(`Imported ${data.import_stats.questions} questions across ${data.import_stats.elements} element(s)`);
      setLinkOpen(false);
      load();
      setEditing(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const saveDraft = async () => {
    try {
      const { data } = await api.put(`/tenants/${tenantId}/questionnaires/${editing.id}`, {
        label: editing.label,
        rounding: editing.rounding,
        elements: editing.elements,
      });
      setEditing(data);
      toast.success("Draft saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const publish = async (id) => {
    try {
      await api.post(`/tenants/${tenantId}/questionnaires/${id}/publish`);
      toast.success("Questionnaire published — cycles can now be pinned to it");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const patchElement = (ei, patch) => {
    const els = [...editing.elements];
    els[ei] = { ...els[ei], ...patch };
    setEditing({ ...editing, elements: els });
  };
  const patchQuestion = (ei, si, qi, patch) => {
    const els = JSON.parse(JSON.stringify(editing.elements));
    els[ei].sub_elements[si].questions[qi] = { ...els[ei].sub_elements[si].questions[qi], ...patch };
    setEditing({ ...editing, elements: els });
  };
  const patchOption = (ei, si, qi, oi, patch) => {
    const els = JSON.parse(JSON.stringify(editing.elements));
    els[ei].sub_elements[si].questions[qi].options[oi] = {
      ...els[ei].sub_elements[si].questions[qi].options[oi],
      ...patch,
    };
    setEditing({ ...editing, elements: els });
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-workbooks-header"
        eyebrow="Administration"
        title="Questionnaire workbooks"
        description="Upload the Panchatanthra and Viveka Kartha workbooks, or pull them from a Drive link. Elements, questions, options and every weight become editable draft configuration — publish to freeze it for scoring."
        actions={
          <div className="flex flex-wrap gap-3">
            <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="import-link-button" variant="outline" className="h-9 border-deepblue/30 text-[10px] uppercase tracking-[0.18em] text-navy">
                  Import from Drive
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Import from Drive folder or file</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-caps">Framework</Label>
                    <Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}>
                      <SelectTrigger data-testid="link-framework-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FRAMEWORKS.map((f) => <SelectItem key={f} value={f}>{FRAMEWORK_META[f].name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Version label</Label>
                    <Input data-testid="link-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="PT-2026.2" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">File or folder link</Label>
                    <Textarea data-testid="link-url-input" rows={2} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://drive.google.com/drive/folders/… or /file/d/…" />
                    <p className="text-[11px] text-muted-foreground">
                      Paste a <strong>folder</strong> link to import every workbook inside it as one version
                      (each workbook becomes a component), or a single <strong>file</strong> link. Sharing must be
                      “Anyone with the link can view”. Google Sheets links export as .xlsx automatically.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Sheet name (optional)</Label>
                    <Input data-testid="link-sheet-input" value={form.sheet_name} onChange={(e) => setForm({ ...form, sheet_name: e.target.value })} placeholder="First sheet used if blank" />
                  </div>
                  <Button data-testid="import-link-submit" className="w-full bg-deepblue text-white hover:bg-navy" disabled={!form.url || busy} onClick={importLink}>
                    {busy ? "Importing…" : "Import as draft"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-workbook-button" className="h-9 bg-deepblue text-[10px] uppercase tracking-[0.18em] text-white hover:bg-navy">
                  Upload workbook
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl text-navy">Upload Excel workbook</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-caps">Framework</Label>
                    <Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}>
                      <SelectTrigger data-testid="upload-framework-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FRAMEWORKS.map((f) => <SelectItem key={f} value={f}>{FRAMEWORK_META[f].name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Version label</Label>
                    <Input data-testid="upload-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="VK-2026.1" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Sheet name (optional)</Label>
                    <Input data-testid="upload-sheet-input" value={form.sheet_name} onChange={(e) => setForm({ ...form, sheet_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Workbook (.xlsx)</Label>
                    <Input data-testid="workbook-file-input" type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <p className="text-[11px] text-muted-foreground">
                      Expected columns: Element, Element Weight, Sub Element, Question, Question Weight,
                      Option 1–5 and Option 1–5 Weight. A long format (Option / Option Weight per row) also works.
                    </p>
                  </div>
                  <Button data-testid="upload-workbook-submit" className="w-full bg-deepblue text-white hover:bg-navy" disabled={!file || busy} onClick={uploadWorkbook}>
                    {busy ? "Parsing…" : "Import as draft"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Tabs defaultValue="versions">
        <TabsList>
          <TabsTrigger data-testid="tab-versions" value="versions" className="text-xs uppercase tracking-[0.15em]">Versions</TabsTrigger>
          <TabsTrigger data-testid="tab-sources" value="sources" className="text-xs uppercase tracking-[0.15em]">Linked sources</TabsTrigger>
        </TabsList>

        <TabsContent value="versions" className="mt-8 space-y-4">
          {FRAMEWORKS.map((fw) => {
            const rows = versions.filter((v) => (v.framework || "panchatanthra") === fw);
            const a = ACCENT[FRAMEWORK_META[fw].accent];
            return (
              <div key={fw} className="panel overflow-hidden" data-testid={`workbook-group-${fw}`}>
                <div className={`h-1 w-full ${a.bar}`} />
                <div className="flex items-center justify-between px-5 py-4">
                  <p className="font-display text-xl text-navy">{FRAMEWORK_META[fw].name}</p>
                  <p className="label-caps">{rows.length} version(s)</p>
                </div>
                <div className="divide-y divide-border border-t border-border">
                  {rows.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-testid={`questionnaire-${v.id}`}>
                      <div>
                        <p className="text-sm">{v.label}</p>
                        <p className="label-caps mt-1">
                          {v.stats.elements} elements · {v.stats.questions} questions
                          {v.import_source ? ` · ${v.import_source.kind === "upload" ? "uploaded" : "linked"}` : ""}
                          {v.import_warnings?.length ? ` · ${v.import_warnings.length} warning(s)` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StateBadge state={v.state} />
                        <Button data-testid={`edit-questionnaire-${v.id}`} size="sm" variant="outline" className="text-[10px] uppercase tracking-[0.15em]" onClick={() => openEditor(v.id)}>
                          {v.state === "published" ? "View" : "Edit"}
                        </Button>
                        {v.state !== "published" && (
                          <>
                            <Button data-testid={`publish-questionnaire-${v.id}`} size="sm" className="bg-teal text-[10px] uppercase tracking-[0.15em] text-white hover:bg-teal/85" onClick={() => publish(v.id)}>
                              Publish
                            </Button>
                            <Button
                              data-testid={`delete-questionnaire-${v.id}`}
                              size="sm"
                              variant="ghost"
                              className="text-destructive"
                              onClick={async () => {
                                await api.delete(`/tenants/${tenantId}/questionnaires/${v.id}`).catch((e) => toast.error(errMsg(e)));
                                setEditing(null);
                                load();
                              }}
                            >
                              <Trash size={14} />
                            </Button>
                          </>
                        )}
                        {v.state === "published" && (
                          <Button
                            data-testid={`duplicate-questionnaire-${v.id}`}
                            size="sm"
                            variant="ghost"
                            className="text-[10px] uppercase tracking-[0.15em]"
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/tenants/${tenantId}/questionnaires/${v.id}/duplicate`, {});
                                toast.success("Duplicated as a new draft");
                                load();
                                openEditor(data.id);
                              } catch (e) {
                                toast.error(errMsg(e));
                              }
                            }}
                          >
                            Duplicate as draft
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {rows.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No workbook imported for this framework yet.</p>}
                </div>
              </div>
            );
          })}
        </TabsContent>

        <TabsContent value="sources" className="mt-8 space-y-6">
          <div className="panel p-6">
            <p className="label-caps mb-4">Register a reusable workbook link</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Input data-testid="source-name-input" placeholder="Source name" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <Select value={form.framework} onValueChange={(v) => setForm({ ...form, framework: v })}>
                <SelectTrigger data-testid="source-framework-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FRAMEWORKS.map((f) => <SelectItem key={f} value={f}>{FRAMEWORK_META[f].name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="source-url-input" placeholder="Drive / Sheets file link" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              <Button
                data-testid="create-source-button"
                className="bg-deepblue text-[10px] uppercase tracking-[0.18em] text-white hover:bg-navy"
                disabled={!form.label || !form.url}
                onClick={async () => {
                  try {
                    await api.post(`/tenants/${tenantId}/workbook-sources`, {
                      name: form.label, framework: form.framework, url: form.url,
                      sheet_name: form.sheet_name || null,
                    });
                    toast.success("Source registered — sync any time to create a fresh draft");
                    load();
                  } catch (e) {
                    toast.error(errMsg(e));
                  }
                }}
              >
                Register source
              </Button>
            </div>
          </div>
          <div className="panel divide-y divide-border" data-testid="workbook-sources-list">
            {sources.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                <div className="min-w-0">
                  <p className="text-sm">{s.name}</p>
                  <p className="label-caps mt-1 truncate">{FRAMEWORK_META[s.framework]?.name} · {s.last_synced_at ? `last synced ${s.last_synced_at.slice(0, 10)}` : "never synced"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`sync-source-${s.id}`}
                    size="sm"
                    variant="outline"
                    className="text-[10px] uppercase tracking-[0.15em]"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/tenants/${tenantId}/workbook-sources/${s.id}/sync`, {});
                        toast.success(`Synced — draft ${data.label} created`);
                        load();
                        setEditing(data);
                      } catch (e) {
                        toast.error(errMsg(e));
                      }
                    }}
                  >
                    Sync now
                  </Button>
                  <Button
                    data-testid={`delete-source-${s.id}`}
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    onClick={async () => {
                      await api.delete(`/tenants/${tenantId}/workbook-sources/${s.id}`).catch((e) => toast.error(errMsg(e)));
                      load();
                    }}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
            {sources.length === 0 && <EmptyState testid="sources-empty" title="No linked sources" message="Register a Drive or Sheets file link so you can re-sync the workbook whenever it changes." />}
          </div>
        </TabsContent>
      </Tabs>

      {editing && (
        <div className="space-y-6" data-testid="questionnaire-editor">
          <div className="panel flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <Label className="label-caps">Version label</Label>
                <Input
                  data-testid="editor-label-input"
                  value={editing.label}
                  disabled={editing.state === "published"}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  className="mt-1 h-9 w-48"
                />
              </div>
              <div>
                <Label className="label-caps">Rounding</Label>
                <Input
                  data-testid="editor-rounding-input"
                  type="number"
                  min={0}
                  max={4}
                  value={editing.rounding ?? 2}
                  disabled={editing.state === "published"}
                  onChange={(e) => setEditing({ ...editing, rounding: Number(e.target.value) })}
                  className="mt-1 h-9 w-24"
                />
              </div>
              <StateBadge state={editing.state} />
            </div>
            <div className="flex items-center gap-2">
              {editing.state !== "published" && (
                <>
                  <Button data-testid="editor-save-button" variant="outline" className="text-[10px] uppercase tracking-[0.15em]" onClick={saveDraft}>
                    <FloppyDisk size={14} className="mr-2" /> Save draft
                  </Button>
                  <Button data-testid="editor-publish-button" className="bg-teal text-[10px] uppercase tracking-[0.15em] text-white hover:bg-teal/85" onClick={() => publish(editing.id)}>
                    Publish
                  </Button>
                </>
              )}
              <Button data-testid="editor-close-button" variant="ghost" className="text-[10px] uppercase tracking-[0.15em]" onClick={() => setEditing(null)}>
                Close
              </Button>
            </div>
          </div>

          {editing.import_warnings?.length > 0 && (
            <div data-testid="import-warnings" className="rounded-lg border-l-4 border-gold bg-gold/8 px-5 py-4">
              <p className="flex items-center gap-2 text-sm font-medium text-gold">
                <WarningCircle size={16} /> {editing.import_warnings.length} import warning(s)
              </p>
              <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                {editing.import_warnings.slice(0, 8).map((w, i) => <li key={i}>• {w}</li>)}
              </ul>
            </div>
          )}

          {(editing.elements || []).map((el, ei) => (
            <div key={el.code || ei} className="panel overflow-hidden" data-testid={`editor-element-${el.code}`}>
              <div className="flex flex-wrap items-center gap-4 border-b border-border bg-sky/10 px-5 py-4">
                <Input
                  data-testid={`element-name-${ei}`}
                  value={el.name}
                  disabled={editing.state === "published"}
                  onChange={(e) => patchElement(ei, { name: e.target.value })}
                  className="h-9 max-w-sm font-display text-lg"
                />
                <div className="flex items-center gap-2">
                  <span className="label-caps">Element weight</span>
                  <Input
                    data-testid={`element-weight-${ei}`}
                    type="number"
                    step="0.05"
                    value={el.weight ?? 1}
                    disabled={editing.state === "published"}
                    onChange={(e) => patchElement(ei, { weight: Number(e.target.value) })}
                    className="h-9 w-24 font-mono tabular-nums"
                  />
                </div>
                <span className="label-caps ml-auto">{el.code}</span>
              </div>
              {(el.sub_elements || []).map((sub, si) => (
                <div key={sub.code || si} className="border-b border-border last:border-0">
                  <p className="label-caps px-5 pt-4">{sub.name}</p>
                  {(sub.questions || []).map((q, qi) => (
                    <div key={`${q.code}-${qi}`} className="px-5 py-4" data-testid={`editor-question-${q.code}`}>
                      <div className="flex flex-wrap items-start gap-3">
                        <Textarea
                          data-testid={`question-text-${q.code}`}
                          value={q.text}
                          rows={2}
                          disabled={editing.state === "published"}
                          onChange={(e) => patchQuestion(ei, si, qi, { text: e.target.value })}
                          className="min-w-[16rem] flex-1 text-sm"
                        />
                        <div>
                          <span className="label-caps">Q weight</span>
                          <Input
                            data-testid={`question-weight-${q.code}`}
                            type="number"
                            step="0.05"
                            value={q.weight ?? 1}
                            disabled={editing.state === "published"}
                            onChange={(e) => patchQuestion(ei, si, qi, { weight: Number(e.target.value) })}
                            className="mt-1 h-9 w-24 font-mono tabular-nums"
                          />
                        </div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-5">
                        {(q.options || []).map((o, oi) => (
                          <div key={oi} className="rounded-md border border-border bg-muted/40 p-2">
                            <Input
                              data-testid={`option-label-${q.code}-${oi}`}
                              value={o.label}
                              disabled={editing.state === "published"}
                              onChange={(e) => patchOption(ei, si, qi, oi, { label: e.target.value })}
                              className="h-8 text-xs"
                            />
                            <div className="mt-2 flex items-center gap-2">
                              <span className="label-caps">Wt</span>
                              <Input
                                data-testid={`option-weight-${q.code}-${oi}`}
                                type="number"
                                step="0.5"
                                value={o.value}
                                disabled={editing.state === "published"}
                                onChange={(e) => patchOption(ei, si, qi, oi, { value: Number(e.target.value) })}
                                className="h-8 w-20 font-mono text-xs tabular-nums"
                              />
                            </div>
                          </div>
                        ))}
                        {(q.options || []).length === 0 && (
                          <p className="text-xs text-destructive md:col-span-5">No options parsed — add them before publishing.</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
